import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  IconDownload,
  IconLoader,
  responseErrorsResolver,
  ResponseError,
  Popup,
  InputText,
} from '@ui/src';
import DocumentsItems from '../documents-items';
import { ApplicationDocument } from '../../../hoc/new-application/types';
import {
  useLazyGetIndividualApplicationDocumentFileQuery,
  useLazyGetCofApplicationDocumentFileQuery,
  useRenameIndividualDocumentMutation,
  useRenameCofDocumentMutation,
} from '../../../hoc/new-application/store/services';
import { IndividualApplicationStepThreeDocumentsTypes } from '../types';
import StepPartBlock from '../corporate/parts/step-part-block';

import cn from '../new-application.module.css';

const StepDocuments = ({
  documents,
  selectedDocumentsCounts,
  onGroupSelect,
  applicationId,
  cof,
  setSelected,
  search,
  status,
  renameAvailable,
}: IndividualApplicationStepThreeDocumentsTypes) => {
  const [renamePopupVisible, setRenamePopupVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    ApplicationDocument | undefined
  >(undefined);
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');

  const {
    setError,
    formState: { errors },
  } = useForm();

  const [
    getIndividualApplicationDocumentFile,
    { isLoading, isSuccess, isError },
  ] = useLazyGetIndividualApplicationDocumentFileQuery();
  const [
    getCofApplicationDocumentFile,
    {
      isLoading: cofDocIsLoading,
      isSuccess: cofDocIsSuccess,
      isError: cofDocIsError,
    },
  ] = useLazyGetCofApplicationDocumentFileQuery();
  const [rename, { isLoading: renameInProgress, isSuccess: renameIsSuccess }] =
    useRenameIndividualDocumentMutation();
  const [
    renameCofDocument,
    {
      isLoading: renameCofDocumentInProgress,
      isSuccess: renameCofDocumentIsSuccess,
    },
  ] = useRenameCofDocumentMutation();

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      let response;

      if (cof) {
        response = (await renameCofDocument({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      } else {
        response = (await rename({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const getFiles = async () => {
    const selectedDocuments = documents?.filter((item) => item.isSelected);

    if (selectedDocuments && selectedDocuments.length) {
      let downloadedFiles;

      if (cof) {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getCofApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      } else {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getIndividualApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      }

      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;

          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = selectedDocuments[index].name;
          hiddenElement.click();
        });
      }
    }
  };

  useEffect(() => {
    if (selectedDocument && !documentName) {
      setDocumentName(selectedDocument.name);
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (renameCofDocumentIsSuccess || renameIsSuccess) {
      setRenamePopupVisible(false);
      setSelectedDocument(undefined);
      setDocumentName('');
      setNameError('');
    }
  }, [renameCofDocumentIsSuccess, renameIsSuccess]);

  useEffect(() => {
    if (isSuccess || isError || cofDocIsSuccess || cofDocIsError) {
      onGroupSelect(false);
    }
  }, [isSuccess, isError, cofDocIsSuccess]);

  let documentsContent;

  if (documents && !documents?.length) {
    documentsContent = (
      <div className={cn.emptyDocuments}>No attached documents yet</div>
    );
  } else if (documents && documents.length > 0) {
    documentsContent = (
      <DocumentsItems
        onGroupSelect={onGroupSelect}
        documents={documents}
        applicationId={applicationId}
        setSelected={setSelected}
        cof={cof}
        search={search}
        setSelectedDocument={setSelectedDocument}
        setRenamePopupVisible={setRenamePopupVisible}
        status={status}
        renameAvailable={renameAvailable}
      />
    );
  }

  return (
    <>
      <StepPartBlock title="Confirming documents" zIndex={10}>
        <div className={cn.documentsTop}>
          <div className={cn.documentsTopLeft}>
            <button
              type="button"
              className={cn.selectedDocumentsButton}
              disabled={
                selectedDocumentsCounts === 0 || isLoading || cofDocIsLoading
              }
              onClick={getFiles}
              tabIndex={-1}
            >
              {isLoading || cofDocIsLoading ? (
                <IconLoader
                  className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                  width={16}
                  height={16}
                />
              ) : (
                <IconDownload
                  className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                  width={16}
                  height={16}
                />
              )}

              {isLoading || cofDocIsLoading ? 'Loading...' : 'Download'}
            </button>
          </div>
        </div>
        {documentsContent}
      </StepPartBlock>

      <Popup
        title="Rename"
        onCancelClick={() => {
          setRenamePopupVisible(false);
          setSelectedDocument(undefined);
          setDocumentName('');
          setNameError('');
        }}
        onOkClick={onRename}
        cancelButtonText="Cancel"
        okButtonText={
          renameInProgress || renameCofDocumentInProgress
            ? 'Loading...'
            : 'Rename'
        }
        isVisible={renamePopupVisible}
        okButtonDisabled={selectedDocument?.name === documentName}
      >
        <InputText
          name="documentName"
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          placeholder="Enter document name"
          labelClassName={cn.renameInput}
          error={nameError || (errors?.name?.message as string)}
        />
      </Popup>
    </>
  );
};

export default StepDocuments;
